'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface FilterCategory {
  title: string;
  options: FilterOption[];
}

interface FilterPanelProps {
  onFilterChange: (filters: SelectedFilters) => void;
}

export interface SelectedFilters {
  categories: string[];
  materials: string[];
  certifications: string[];
  capacity: string[];
}

const FilterPanel = ({ onFilterChange }: FilterPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    categories: [],
    materials: [],
    certifications: [],
    capacity: []
  });

  const filterCategories: Record<string, FilterCategory> = {
    categories: {
      title: 'Manufacturing Process',
      options: [
        { id: 'cnc', label: 'CNC Machining', count: 12 },
        { id: 'additive', label: 'Additive Manufacturing', count: 8 },
        { id: 'sheet-metal', label: 'Sheet Metal Fabrication', count: 10 },
        { id: 'composites', label: 'Composite Manufacturing', count: 6 },
        { id: 'testing', label: 'Testing & Validation', count: 9 }
      ]
    },
    materials: {
      title: 'Material Capabilities',
      options: [
        { id: 'aluminum', label: 'Aluminum Alloys', count: 15 },
        { id: 'titanium', label: 'Titanium', count: 8 },
        { id: 'stainless', label: 'Stainless Steel', count: 12 },
        { id: 'composites', label: 'Carbon Fiber Composites', count: 6 },
        { id: 'specialty', label: 'Specialty Alloys', count: 7 }
      ]
    },
    certifications: {
      title: 'Certifications',
      options: [
        { id: 'as9100', label: 'AS9100 Rev D', count: 18 },
        { id: 'itar', label: 'ITAR Compliant', count: 14 },
        { id: 'nadcap', label: 'NADCAP Accredited', count: 10 },
        { id: 'iso', label: 'ISO 9001:2015', count: 16 }
      ]
    },
    capacity: {
      title: 'Availability',
      options: [
        { id: 'high', label: 'High Availability', count: 8 },
        { id: 'medium', label: 'Moderate Availability', count: 6 },
        { id: 'low', label: 'Limited Availability', count: 4 }
      ]
    }
  };

  const handleFilterToggle = (category: keyof SelectedFilters, optionId: string) => {
    const newFilters = { ...selectedFilters };
    const categoryFilters = newFilters[category];
    
    if (categoryFilters.includes(optionId)) {
      newFilters[category] = categoryFilters.filter(id => id !== optionId);
    } else {
      newFilters[category] = [...categoryFilters, optionId];
    }
    
    setSelectedFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    const emptyFilters: SelectedFilters = {
      categories: [],
      materials: [],
      certifications: [],
      capacity: []
    };
    setSelectedFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const getTotalActiveFilters = () => {
    return Object.values(selectedFilters).reduce((sum, arr) => sum + arr.length, 0);
  };

  return (
    <>
      {/* Mobile Filter Toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed bottom-6 right-6 z-40 w-14 h-14 bg-accent rounded-full flex items-center justify-center shadow-aerospace hover:shadow-glow transition-all duration-300"
      >
        <Icon name="AdjustmentsHorizontalIcon" size={24} className="text-primary" />
        {getTotalActiveFilters() > 0 && (
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-mission-orange rounded-full flex items-center justify-center">
            <span className="font-cta text-xs text-primary">{getTotalActiveFilters()}</span>
          </div>
        )}
      </button>

      {/* Filter Panel */}
      <div
        className={`fixed lg:sticky top-0 left-0 h-screen lg:h-auto w-80 bg-card border-r lg:border-r-0 lg:border border-border rounded-none lg:rounded-lg p-6 overflow-y-auto z-50 transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-headline text-xl font-bold text-foreground">Filters</h2>
            {getTotalActiveFilters() > 0 && (
              <span className="font-cta text-xs text-technical-gray">
                {getTotalActiveFilters()} active
              </span>
            )}
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors duration-300"
          >
            <Icon name="XMarkIcon" size={20} className="text-foreground" />
          </button>
        </div>

        {/* Clear All */}
        {getTotalActiveFilters() > 0 && (
          <button
            onClick={clearAllFilters}
            className="w-full mb-4 px-4 py-2 bg-muted text-accent font-cta text-xs rounded-lg hover:bg-accent/10 transition-all duration-300"
          >
            CLEAR ALL FILTERS
          </button>
        )}

        {/* Filter Categories */}
        <div className="space-y-6">
          {Object.entries(filterCategories).map(([categoryKey, category]) => (
            <div key={categoryKey} className="border-b border-border pb-6 last:border-b-0">
              <h3 className="font-headline text-sm font-semibold text-foreground mb-3">
                {category.title}
              </h3>
              <div className="space-y-2">
                {category.options.map((option) => {
                  const isSelected = selectedFilters[categoryKey as keyof SelectedFilters].includes(option.id);
                  return (
                    <label
                      key={option.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted cursor-pointer transition-colors duration-300 group"
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-300 ${
                            isSelected
                              ? 'bg-accent border-accent' :'border-border group-hover:border-accent'
                          }`}
                        >
                          {isSelected && (
                            <Icon name="CheckIcon" size={14} className="text-primary" />
                          )}
                        </div>
                        <span className="font-body text-sm text-foreground">{option.label}</span>
                      </div>
                      <span className="font-cta text-xs text-technical-gray">
                        {option.count}
                      </span>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleFilterToggle(categoryKey as keyof SelectedFilters, option.id)}
                        className="sr-only"
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Apply Button (Mobile) */}
        <button
          onClick={() => setIsOpen(false)}
          className="lg:hidden w-full mt-6 px-4 py-3 bg-mission-orange text-primary font-cta text-sm rounded-lg hover:shadow-glow transition-all duration-300"
        >
          APPLY FILTERS
        </button>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          className="lg:hidden fixed inset-0 bg-primary/80 backdrop-blur-sm z-40"
        ></div>
      )}
    </>
  );
};

export default FilterPanel;